import { AnalyticsEvents, SocialPublishTappedShape } from '@mondaygpt/analytics-events'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  PinterestIcon,
} from 'components'
import { useAnalytics } from 'hooks'
import { useIsMobile } from 'hooks/use-mobile'
import {
  ArrowLeft,
  Calendar,
  CalendarOff,
  Check,
  Clipboard,
  Download,
  Hash,
  Instagram,
  Linkedin,
  Loader2,
  MoreHorizontal,
  Send,
  Trash2,
  Youtube,
} from 'lucide-react'
import { selectBusinessId } from 'modules/authentication'
import { useSubscriptionGate } from 'modules/billing'
import { collectDownloadableMedia, downloadCreativeMedia } from '../utils/download-media'
import { usePlatformRolloutEnabled } from 'modules/feature-flags'
import { useAppSelector } from 'modules/core'
import { oauthUtils } from 'modules/oauth/utils/oauth-utils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { entrySourceFromLocationState } from '../utils/post-entry-source'
import { errorReportingService } from 'services/error-reporting/error-reporting.service'
import { CreativeService } from '../../../openapi/services/CreativeService'
import { IntegrationsService } from '../../../openapi/services/IntegrationsService'
import { useCalendarStore } from '../../calendar/store/calendar-store'
import { ScheduleDialog, formatScheduledDate } from '../../calendar/components/ScheduleDialog'
import { useBrandTimezone, useScheduleTimezone } from '../../common/hooks/use-brand-timezone'
import { useEnsureBrandKitTimezone } from '../../common/hooks/use-ensure-brand-kit-timezone'
import { splitIsoInTz } from '../../common/utils/business-timezone'
import { usePlatformPublish } from '../hooks/usePlatformPublish'
import { toast } from 'sonner'
import { takeOffScheduleForManualPublish } from '../utils/publish-creative'
import { flushPersistsAndWait, useCreativeStore } from '../store/creative-store'
import { creativeTypeToPlatform, useIdeaStore } from '../store/idea-store'
import { usePublishStatusStore } from '../store/publish-status-store'
import { ConnectToPublishDialog } from './ConnectToPublishDialog'
import { PlatformConnectDialog } from './PlatformConnectDialog'
import { PostModal } from './PostModal'
import { TikTokPostDialog } from './TikTokPostDialog'
import { ScheduleSplitButton } from './ScheduleSplitButton'
import { XLogo } from './XLogo'
import { validateInstagramCarousel } from '../utils/validateInstagramCarousel'
import { platformContentErrorMessage } from '../utils/validatePlatformContent'
import {
  Creative,
  CreativeType,
  CREATOR_MODE_MAP,
  type FacebookPostContent,
  type InstagramPostContent,
  type LinkedInPostContent,
  type PinterestPinContent,
  type TikTokVideoContent,
  type XPostContent,
  type YouTubeShortsContent,
} from '../types'
import { computeInstagramContentHash } from '../utils/content-hash'
import { extractApiError } from '../utils/extract-api-error'
import { CreativeTypeBadge } from './CreativeTypeBadge'
import type { LivePlatform, PostIdea } from '../store/idea-store'

interface ComposerHeaderProps {
  creative: Creative
  postContext?: {
    idea: PostIdea
    activePlatform: LivePlatform
  }
  /** In-strip selection from ComposerPage. Drives the multi-platform Schedule
   *  flow (the connect gate + the confirmIdea fan-out). Undefined in standalone
   *  mode, where the legacy per-creative Schedule path stays put. */
  selectedPlatforms?: LivePlatform[]
  /** ISO 8601 date+time staged in a parent surface (e.g. PostModal) and passed
   *  through via location.state. Pre-fills the Schedule dialog when the
   *  creative isn't yet committed-scheduled. Does NOT flip the "Scheduled for X"
   *  pill — that stays driven by committed `creative.scheduledAt`. */
  stagedScheduledAt?: string
}

export function ComposerHeader({
  creative,
  postContext,
  selectedPlatforms,
  stagedScheduledAt,
}: ComposerHeaderProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useIsMobile()
  const { track } = useAnalytics()
  const { gate } = useSubscriptionGate()
  // Scheduling zones: capture the brand TZ if unset (so display==input zone),
  // then resolve the display zone (`displayTz`, for labels) and the input zone
  // (`scheduleTz`, for seeding the picker — matches what ScheduleDialog writes).
  useEnsureBrandKitTimezone()
  const displayTz = useBrandTimezone()
  const scheduleTz = useScheduleTimezone()
  const isRolloutOn = usePlatformRolloutEnabled()
  const updateCreative = useCreativeStore(s => s.updateCreative)
  const deleteCreative = useCreativeStore(s => s.deleteCreative)
  const deletePost = useIdeaStore(s => s.deletePost)
  const unschedulePostIdea = useIdeaStore(s => s.unschedule)
  // Journey origin (post_scheduled.entry_source) threaded through
  // location.state by upstream surfaces (Home/Posts/Calendar/Cockpit). Falls
  // back to 'composer_direct' for deep-link entries (URL paste, refresh).
  // Snapshotted on first mount because intra-composer navs (tab switch, URL
  // auto-promote) use `replace: true` which wipes location.state — without
  // the snapshot the eventual Post Scheduled emit would degrade to
  // 'composer_direct' for anyone who tabbed between platforms before clicking
  // Schedule.
  const journeyEntrySourceRef = useRef(
    entrySourceFromLocationState(location.state) ?? 'composer_direct'
  )
  const journeyEntrySource = journeyEntrySourceRef.current

  const [isEditingName, setIsEditingName] = useState(false)
  const [editName, setEditName] = useState(creative.name)
  const [showActions, setShowActions] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showScheduleDialog, setShowScheduleDialog] = useState(false)
  // Post-mode Schedule entry → PostModal scoped to the composer's in-strip
  // platform set (lockedPlatforms). PostModal owns the date/time picker and
  // calls back into us via `onConnectRequired` when any selected platform
  // needs OAuth before scheduling.
  const [showSchedulePostModal, setShowSchedulePostModal] = useState(false)
  // How PostModal opens: `'schedule'` shows the scheduling surface; `'post_now'`
  // (split-button caret) auto-opens PostModal's post-now confirmation. Reset to
  // `'schedule'` whenever the modal closes so the next open defaults correctly.
  const [postModalIntent, setPostModalIntent] = useState<'schedule' | 'post_now'>('schedule')
  // One-shot "just scheduled" flag — drives the acid pulse on the Schedule
  // button right after PostModal closes with scheduled=true. Auto-resets
  // after the pulse so it doesn't replay on subsequent renders.
  const [justScheduled, setJustScheduled] = useState(false)
  // Connect-gate state for PostModal's `onConnectRequired`. We hold the
  // promise's resolve/reject so the ConnectToPublishDialog's onReady /
  // close-without-ready callbacks can settle the awaited handoff.
  const [connectGate, setConnectGate] = useState<{
    open: boolean
    missing: LivePlatform[]
    selected: LivePlatform[]
  }>({ open: false, missing: [], selected: [] })
  const connectGateResolversRef = useRef<{
    resolve: (platforms: LivePlatform[]) => void
    reject: () => void
  } | null>(null)
  const nameInputRef = useRef<HTMLInputElement>(null)
  const actionsRef = useRef<HTMLDivElement>(null)

  // Schedule click handler. Standalone mode opens the legacy ScheduleDialog
  // directly; Post mode opens PostModal scoped to the composer's in-strip
  // platforms (PostModal handles the date/time picker AND the connect gate).
  // The active platform's creative is the unit of scheduling here, so gating on
  // its own publish state is inherently per-platform: switching to an
  // unpublished platform tab re-enables scheduling for that one. The server
  // (calendar.scheduleCreative / confirmPost) enforces the same rule.
  const isActiveCreativePublished = !!creative.publishedAt
  // Live permalink for the active creative — turns the published CTA into a
  // "Live" link. Mirrors extractPermalinkFromContent (X synthesizes from id).
  const activeLivePermalink = (() => {
    const c = (creative.content ?? {}) as Record<string, any>
    const xTweetId: string | undefined = c.xPublish?.tweetId
    return (
      c.instagramPublish?.permalink ??
      c.facebookPublish?.permalink ??
      c.linkedinPublish?.permalink ??
      c.youtubePublish?.permalink ??
      c.pinterestPublish?.permalink ??
      c.tiktokPublish?.permalink ??
      (xTweetId ? `https://x.com/i/web/status/${xTweetId}` : undefined)
    )
  })()
  // Approval is a post-level concept (review-mode autopilot). A needs-approval
  // post is already scheduled — the actionable thing is to approve it, optionally
  // tweaking the date first. Standalone/user posts never require approval.
  const needsApproval =
    !!postContext && !!postContext.idea.requiresApproval && !postContext.idea.approvedAt
  const handleScheduleClick = useCallback(() => {
    if (isActiveCreativePublished) return
    if (!postContext) {
      setShowScheduleDialog(true)
      return
    }
    // Approval flow (review-mode autopilot) rides the same PostModal as
    // Schedule: it renders the acid Approve CTA, and approving carries the
    // strip's platform selection + slot in ONE server transaction, behind the
    // same connect gate. The old date-only dialog stamped approval without the
    // selection ("an autopilot post is already confirmed" was the premise, and
    // it was false), so a platform added in the strip stayed a candidate
    // preview and never published. An empty strip is fine for approval:
    // PostModal falls back to its default seed when lockedPlatforms is empty.
    const platforms = selectedPlatforms ?? []
    if (platforms.length === 0 && !needsApproval) return
    setPostModalIntent('schedule')
    setShowSchedulePostModal(true)
  }, [isActiveCreativePublished, needsApproval, postContext, selectedPlatforms])

  // Split-button caret "Post now" → open PostModal in post-now intent (it
  // auto-opens the publish confirmation). Post mode only; the caret never
  // renders in standalone mode, and an already-published creative is a no-op.
  const handlePostNow = useCallback(() => {
    if (isActiveCreativePublished) return
    if (!postContext) return
    const platforms = selectedPlatforms ?? []
    if (platforms.length === 0) return
    setPostModalIntent('post_now')
    setShowSchedulePostModal(true)
  }, [isActiveCreativePublished, postContext, selectedPlatforms])

  // Bridge PostModal's `onConnectRequired` Promise to ConnectToPublishDialog.
  // PostModal awaits; we open the dialog, capture the resolve/reject, and let
  // the dialog's onReady (Continue) or onOpenChange(false) (cancel) settle
  // the promise.
  const handleConnectRequired = useCallback(
    (missing: LivePlatform[]) => {
      const selected = selectedPlatforms ?? []
      return new Promise<LivePlatform[]>((resolve, reject) => {
        connectGateResolversRef.current = { resolve, reject }
        setConnectGate({ open: true, missing, selected })
      })
    },
    [selectedPlatforms]
  )

  const handleConnectGateReady = useCallback((finalPlatforms: LivePlatform[]) => {
    connectGateResolversRef.current?.resolve(finalPlatforms)
    connectGateResolversRef.current = null
    setConnectGate(prev => ({ ...prev, open: false }))
  }, [])

  const handleConnectGateOpenChange = useCallback((open: boolean) => {
    if (open) return
    // Dialog closed without onReady firing → treat as cancel.
    connectGateResolversRef.current?.reject()
    connectGateResolversRef.current = null
    setConnectGate(prev => ({ ...prev, open: false }))
  }, [])

  // Focus input when editing
  useEffect(() => {
    if (isEditingName) {
      nameInputRef.current?.focus()
      nameInputRef.current?.select()
    }
  }, [isEditingName])

  // Close menu on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target as Node)) {
        setShowActions(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleSaveName = () => {
    const trimmed = editName.trim()
    if (trimmed && trimmed !== creative.name) {
      updateCreative(creative.id, { name: trimmed })
      track(AnalyticsEvents.Composer.COMPOSER_RENAME_SAVED, {
        creative_id: creative.id,
      })
    } else {
      setEditName(creative.name)
    }
    setIsEditingName(false)
  }

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSaveName()
    if (e.key === 'Escape') {
      setEditName(creative.name)
      setIsEditingName(false)
    }
  }

  // Tap on the trash icon in the actions menu. We open a confirmation dialog
  // and only fire the delete on confirm. The previous implementation
  // navigate('/')-then-await-deleteCreative had two bugs: (1) it deleted a
  // single Creative even in Post mode, leaving sibling platforms alive so
  // the "post" reappeared in the Weekly Cockpit under a different
  // representative; (2) it navigated before awaiting the API, so any server
  // failure landed on an unmounted component with no toast — the optimistic
  // removal made it look successful while the row survived in the DB.
  const handleDelete = () => {
    track(AnalyticsEvents.Composer.COMPOSER_DELETE_TAPPED, {
      creative_id: creative.id,
      creative_type: creative.type,
    })
    setShowActions(false)
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    if (isDeleting) return
    setIsDeleting(true)
    try {
      if (postContext) {
        // Post mode: delete the wrapping Project so the FK cascade takes all
        // platform siblings with it. Matches the user-visible promise of the
        // Delete button ("delete this post").
        await deletePost(postContext.idea.projectId)
      } else {
        // Standalone mode (legacy / Calendar quick-create rollback): delete
        // the single Creative.
        await deleteCreative(creative.id)
      }
      setShowDeleteConfirm(false)
      navigate('/')
    } catch (err) {
      errorReportingService.captureException(err instanceof Error ? err : new Error(String(err)), {
        feature: 'composer_delete',
        creativeId: creative.id,
        ...(postContext ? { projectId: postContext.idea.projectId } : {}),
      })
      toast.error("We couldn't delete this post. Please try again.")
    } finally {
      setIsDeleting(false)
    }
  }

  const businessId = useAppSelector(selectBusinessId)

  // ─── Instagram Publish State ─────────────────────────────────────────
  const isInstagramPost = creative.type === CreativeType.InstagramPost
  const igContent = isInstagramPost ? (creative.content as InstagramPostContent) : null
  const igPublishMeta = igContent?.instagramPublish ?? null
  const igCarouselValidation = useMemo(() => {
    const items = igContent?.mediaItems ?? []
    if (items.length < 2) return { ok: true as const }
    return validateInstagramCarousel(items)
  }, [igContent?.mediaItems])

  const [currentContentHash, setCurrentContentHash] = useState<string | null>(null)
  useEffect(() => {
    if (!igContent) return
    let cancelled = false
    computeInstagramContentHash(igContent).then(hash => {
      if (!cancelled) setCurrentContentHash(hash)
    })
    return () => {
      cancelled = true
    }
  }, [igContent])

  const [showConnectDialog, setShowConnectDialog] = useState(false)
  const [isConnectingIg, setIsConnectingIg] = useState(false)
  const [connectError, setConnectError] = useState<string | null>(null)
  const [isPublishing, setIsPublishing] = useState(false)
  const [publishError, setPublishError] = useState<string | null>(null)
  const [showPostConfirm, setShowPostConfirm] = useState(false)

  const updateCreativeContent = useCreativeStore(s => s.updateCreativeContent)

  const handlePublishToInstagram = useCallback(async () => {
    setIsPublishing(true)
    setPublishError(null)

    try {
      // Off its future slot first (see takeOffScheduleForManualPublish); the
      // server refuses a manual publish of a still-scheduled post.
      if (!(await takeOffScheduleForManualPublish(creative.id))) return
      const result = await CreativeService.creativeControllerPublishToInstagramV1(creative.id)
      if (result.success) {
        if (currentContentHash) {
          updateCreativeContent(
            creative.id,
            {
              instagramPublish: {
                lastPublishedAt: new Date().toISOString(),
                permalink: result.permalink,
                mediaId: result.mediaId,
                contentHash: currentContentHash,
              },
            },
            { userEdit: false }
          )
        }
      } else {
        const errorMsg = result.error || 'Publishing failed'
        setPublishError(errorMsg)
      }
    } catch (error) {
      const message = extractApiError(error)
      setPublishError(message)

      errorReportingService.captureException(error instanceof Error ? error : message, {
        feature: 'instagram_publish',
        creativeId: creative.id,
      })
    } finally {
      setIsPublishing(false)
    }
  }, [creative.id, currentContentHash, updateCreativeContent])

  const handlePostClick = useCallback(async () => {
    if (!gate('publish_instagram')) return
    if (!igCarouselValidation.ok) {
      setPublishError(igCarouselValidation.message)
      return
    }
    // Platform content constraints — same shared validator (and same sentence)
    // the server enforces on every publish path, run here so the user is told
    // before the request instead of after a provider rejection.
    const contentError = platformContentErrorMessage([
      { type: creative.type, content: creative.content },
    ])
    if (contentError) {
      setPublishError(contentError)
      return
    }
    const igTappedShape: (typeof SocialPublishTappedShape)[keyof typeof SocialPublishTappedShape] =
      (igContent?.mediaItems?.length ?? 0) >= 2
        ? SocialPublishTappedShape.CAROUSEL
        : igContent?.videoAttachmentId
          ? SocialPublishTappedShape.SHORT_VERTICAL_VIDEO
          : SocialPublishTappedShape.STATIC_IMAGE
    track(AnalyticsEvents.Social.SOCIAL_PUBLISH_TAPPED, {
      platform: 'instagram',
      content_type: 'image',
      creative_id: creative.id,
      shape: igTappedShape,
      source: 'composer',
    })
    setPublishError(null)

    try {
      const status =
        await IntegrationsService.integrationControllerGetIntegrationStatusV1('instagram')
      if (status.isConnected) {
        handlePublishToInstagram()
      } else {
        setShowConnectDialog(true)
        setConnectError(null)
        track(AnalyticsEvents.Social.SOCIAL_CONNECT_DIALOG_VIEWED, {
          platform: 'instagram',
          creative_id: creative.id,
          source: 'composer',
        })
      }
    } catch {
      setShowConnectDialog(true)
      setConnectError(null)
      track(AnalyticsEvents.Social.SOCIAL_CONNECT_DIALOG_VIEWED, {
        platform: 'instagram',
        creative_id: creative.id,
        source: 'composer',
      })
    }
  }, [
    track,
    creative.id,
    creative.type,
    creative.content,
    handlePublishToInstagram,
    gate,
    igCarouselValidation,
  ])

  const handleConnectInstagram = useCallback(async () => {
    if (!businessId) return
    setIsConnectingIg(true)
    setConnectError(null)
    try {
      const result = await oauthUtils.openOAuthPopup('instagram', {
        businessId,
        source: 'composer',
        track,
      })
      if (result === false) {
        setConnectError('Connection was cancelled or failed. Please try again.')
        track(AnalyticsEvents.Chat.INTEGRATION_CONNECT_FAILED, {
          provider: 'instagram',
          source: 'composer',
          error: 'oauth_popup_failed',
          error_code: 'oauth_popup_failed',
          error_type: 'popup',
        })
        setIsConnectingIg(false)
        return
      }

      // Bridge the ~800ms gap between popup self-close (localStorage path)
      // and final integration persistence. Otherwise the status check below
      // can return isConnected:false on a successful OAuth and we'd surface
      // a "Connection cancelled" toast on a working flow.
      await oauthUtils.waitForOAuthPersistence('instagram')

      try {
        const status =
          await IntegrationsService.integrationControllerGetIntegrationStatusV1('instagram')
        if (status.isConnected) {
          track(AnalyticsEvents.Chat.INTEGRATION_CONNECT_SUCCESS, {
            provider: 'instagram',
            source: 'composer',
          })
          setShowConnectDialog(false)
          handlePublishToInstagram()
        } else {
          setConnectError('Connection was cancelled or failed. Please try again.')
          track(AnalyticsEvents.Chat.INTEGRATION_CONNECT_FAILED, {
            provider: 'instagram',
            source: 'composer',
            error: 'verification_not_connected',
            error_code: 'verification_not_connected',
            error_type: 'verification',
          })
        }
      } catch {
        setConnectError('Could not verify connection. Please try again.')
        track(AnalyticsEvents.Chat.INTEGRATION_CONNECT_FAILED, {
          provider: 'instagram',
          source: 'composer',
          error: 'verification_check_failed',
          error_code: 'verification_check_failed',
          error_type: 'network',
        })
      }
    } catch {
      setConnectError('Something went wrong connecting to Instagram.')
      track(AnalyticsEvents.Chat.INTEGRATION_CONNECT_FAILED, {
        provider: 'instagram',
        source: 'composer',
        error: 'oauth_flow_error',
        error_code: 'oauth_flow_error',
        error_type: 'unknown',
      })
    } finally {
      setIsConnectingIg(false)
    }
  }, [businessId, track, handlePublishToInstagram])

  // ─── TikTok Publish State ──────────────────────────────────────────────
  const isTikTokVideo = creative.type === CreativeType.TikTokVideo
  const tiktokContent = isTikTokVideo ? (creative.content as TikTokVideoContent) : null
  const tiktokHasVideo = !!(tiktokContent?.videoUrl || tiktokContent?.videoAttachmentId)

  // TikTok's Content Sharing Guidelines require the user to choose audience,
  // interactions and commercial disclosure per post (no defaults) before
  // anything is sent. The dialog opens once the connection check passes; its
  // Post button persists the choices and then publishes.
  const [showTikTokPostDialog, setShowTikTokPostDialog] = useState(false)

  const tiktokPublishStatus = usePublishStatusStore(s => s.statuses[creative.id])
  const setTikTokPublishing = usePublishStatusStore(s => s.setPublishing)
  const clearTikTokPublishStatus = usePublishStatusStore(s => s.clearStatus)
  const isTikTokPublishing = tiktokPublishStatus?.status === 'publishing'
  const tikTokPublishError =
    tiktokPublishStatus?.status === 'failed'
      ? (tiktokPublishStatus.error ?? 'Publishing failed')
      : null
  const tiktokPublishSuccess = tiktokPublishStatus?.status === 'success'

  // Clear error after 10 seconds
  useEffect(() => {
    if (tikTokPublishError) {
      const timer = setTimeout(() => clearTikTokPublishStatus(creative.id), 10_000)
      return () => clearTimeout(timer)
    }
  }, [tikTokPublishError, creative.id, clearTikTokPublishStatus])

  const handlePublishToTikTok = useCallback(
    async (postSettings: TikTokVideoContent['postSettings']) => {
      setTikTokPublishing(creative.id, 'tiktok')

      try {
        // Persist the user's per-post TikTok choices BEFORE the publish call —
        // the server reads them from the creative and refuses a post without them.
        if (postSettings) {
          updateCreativeContent(creative.id, { postSettings })
          await flushPersistsAndWait([creative.id])
        }
        // Off its future slot first (see takeOffScheduleForManualPublish); the
        // server refuses a manual publish of a still-scheduled post.
        if (!(await takeOffScheduleForManualPublish(creative.id))) return
        await CreativeService.creativeControllerPublishToTikTokV1(creative.id)
        setShowTikTokPostDialog(false)
        // Server returns 202 immediately — Pusher will notify on completion
      } catch (error) {
        const message = extractApiError(error)
        usePublishStatusStore.getState().setComplete(creative.id, {
          platform: 'tiktok',
          success: false,
          error: message,
          retryable: true,
        })
        errorReportingService.captureException(error instanceof Error ? error : message, {
          feature: 'tiktok_publish',
          creativeId: creative.id,
        })
      }
    },
    [creative.id, setTikTokPublishing, updateCreativeContent]
  )

  const tikTokPlatform = usePlatformPublish({
    provider: 'tiktok',
    creativeId: creative.id,
    // Connected → open the per-post settings dialog; it publishes on Post.
    publishFn: () => setShowTikTokPostDialog(true),
    beforeStatusCheck: useCallback(() => {
      if (!isRolloutOn('tiktok')) return false
      if (!gate('publish_tiktok')) return false
      track(AnalyticsEvents.Social.SOCIAL_PUBLISH_TAPPED, {
        platform: 'tiktok',
        content_type: 'video',
        creative_id: creative.id,
        shape: SocialPublishTappedShape.SHORT_VERTICAL_VIDEO,
        source: 'composer',
      })
      clearTikTokPublishStatus(creative.id)
      return true
    }, [gate, track, isRolloutOn, clearTikTokPublishStatus, creative.id]),
    providerDisplayName: 'TikTok',
  })
  const handleTikTokPostClick = tikTokPlatform.handlePostClick

  // ─── YouTube Publish State ──────────────────────────────────────────
  const [showYouTubeConnectDialog, setShowYouTubeConnectDialog] = useState(false)
  const [isConnectingYouTube, setIsConnectingYouTube] = useState(false)
  const [youTubeConnectError, setYouTubeConnectError] = useState<string | null>(null)
  const [showYouTubeComingSoon, setShowYouTubeComingSoon] = useState(false)
  const youtubePublishStatus = usePublishStatusStore(s => s.statuses[creative.id])
  const setYouTubePublishing = usePublishStatusStore(s => s.setPublishing)
  const clearYouTubePublishStatus = usePublishStatusStore(s => s.clearStatus)
  const isYouTubePublishing =
    creative.type === CreativeType.YouTubeShort && youtubePublishStatus?.status === 'publishing'
  const youTubePublishError =
    creative.type === CreativeType.YouTubeShort && youtubePublishStatus?.status === 'failed'
      ? (youtubePublishStatus.error ?? 'Publishing failed')
      : null
  const youtubePublishSuccess =
    creative.type === CreativeType.YouTubeShort && youtubePublishStatus?.status === 'success'

  useEffect(() => {
    if (youTubePublishError) {
      const timer = setTimeout(() => clearYouTubePublishStatus(creative.id), 10_000)
      return () => clearTimeout(timer)
    }
  }, [youTubePublishError, creative.id, clearYouTubePublishStatus])

  const handlePublishToYouTube = useCallback(async () => {
    setYouTubePublishing(creative.id, 'youtube')

    try {
      // Off its future slot first (see takeOffScheduleForManualPublish); the
      // server refuses a manual publish of a still-scheduled post.
      if (!(await takeOffScheduleForManualPublish(creative.id))) return
      await CreativeService.creativeControllerPublishToYouTubeV1(creative.id)
    } catch (error) {
      const message = extractApiError(error)
      usePublishStatusStore.getState().setComplete(creative.id, {
        platform: 'youtube',
        success: false,
        error: message,
        retryable: true,
      })
      errorReportingService.captureException(error instanceof Error ? error : message, {
        feature: 'youtube_publish',
        creativeId: creative.id,
      })
    }
  }, [creative.id, setYouTubePublishing])

  const handleYouTubePostClick = useCallback(async () => {
    if (!gate('publish_youtube')) return
    track(AnalyticsEvents.Social.SOCIAL_PUBLISH_TAPPED, {
      platform: 'youtube',
      content_type: 'video',
      creative_id: creative.id,
      shape: SocialPublishTappedShape.SHORT_VERTICAL_VIDEO,
      source: 'composer',
    })
    if (!isRolloutOn('youtube')) {
      setShowYouTubeComingSoon(true)
      track(AnalyticsEvents.Social.SOCIAL_COMING_SOON_VIEWED, {
        platform: 'youtube',
        creative_id: creative.id,
        source: 'composer',
      })
      return
    }
    clearYouTubePublishStatus(creative.id)

    try {
      const status =
        await IntegrationsService.integrationControllerGetIntegrationStatusV1('youtube')
      if (status.isConnected) {
        handlePublishToYouTube()
      } else {
        setShowYouTubeConnectDialog(true)
        setYouTubeConnectError(null)
        track(AnalyticsEvents.Social.SOCIAL_CONNECT_DIALOG_VIEWED, {
          platform: 'youtube',
          creative_id: creative.id,
          source: 'composer',
        })
      }
    } catch {
      setShowYouTubeConnectDialog(true)
      setYouTubeConnectError(null)
    }
  }, [handlePublishToYouTube, isRolloutOn, clearYouTubePublishStatus, creative.id, gate, track])

  const handleConnectYouTube = useCallback(async () => {
    if (!businessId) return
    setIsConnectingYouTube(true)
    setYouTubeConnectError(null)
    try {
      const result = await oauthUtils.openOAuthPopup('youtube', {
        businessId,
        source: 'composer',
        track,
      })
      if (result === false) {
        setYouTubeConnectError('Connection was cancelled or failed. Please try again.')
        track(AnalyticsEvents.Chat.INTEGRATION_CONNECT_FAILED, {
          provider: 'youtube',
          source: 'composer',
          error: 'oauth_popup_failed',
          error_code: 'oauth_popup_failed',
          error_type: 'popup',
        })
        setIsConnectingYouTube(false)
        return
      }
      // See handleConnectInstagram above — same persistence race.
      await oauthUtils.waitForOAuthPersistence('youtube')
      try {
        const status =
          await IntegrationsService.integrationControllerGetIntegrationStatusV1('youtube')
        if (status.isConnected) {
          track(AnalyticsEvents.Chat.INTEGRATION_CONNECT_SUCCESS, {
            provider: 'youtube',
            source: 'composer',
          })
          setShowYouTubeConnectDialog(false)
          handlePublishToYouTube()
        } else {
          setYouTubeConnectError('Connection was cancelled or failed. Please try again.')
          track(AnalyticsEvents.Chat.INTEGRATION_CONNECT_FAILED, {
            provider: 'youtube',
            source: 'composer',
            error: 'verification_not_connected',
            error_code: 'verification_not_connected',
            error_type: 'verification',
          })
        }
      } catch {
        setYouTubeConnectError('Could not verify connection. Please try again.')
        track(AnalyticsEvents.Chat.INTEGRATION_CONNECT_FAILED, {
          provider: 'youtube',
          source: 'composer',
          error: 'verification_check_failed',
          error_code: 'verification_check_failed',
          error_type: 'network',
        })
      }
    } catch {
      setYouTubeConnectError('Something went wrong connecting to YouTube.')
      track(AnalyticsEvents.Chat.INTEGRATION_CONNECT_FAILED, {
        provider: 'youtube',
        source: 'composer',
        error: 'oauth_flow_error',
        error_code: 'oauth_flow_error',
        error_type: 'unknown',
      })
    } finally {
      setIsConnectingYouTube(false)
    }
  }, [businessId, handlePublishToYouTube, track])

  // ─── Facebook Publish State ─────────────────────────────────────────
  const isFacebookPost = creative.type === CreativeType.FacebookPost
  const fbContent = isFacebookPost ? (creative.content as FacebookPostContent) : null
  const fbPublishMeta = fbContent?.facebookPublish ?? null

  const [fbPublishing, setFbPublishing] = useState(false)
  const [fbPublishError, setFbPublishError] = useState<string | null>(null)

  const isLiveOnFacebook = !!fbPublishMeta?.lastPublishedAt

  const handlePublishToFacebook = useCallback(async () => {
    setFbPublishing(true)
    setFbPublishError(null)

    try {
      // Off its future slot first (see takeOffScheduleForManualPublish); the
      // server refuses a manual publish of a still-scheduled post.
      if (!(await takeOffScheduleForManualPublish(creative.id))) return
      const result = await CreativeService.creativeControllerPublishToFacebookV1(creative.id)
      if (result.success) {
        updateCreativeContent(
          creative.id,
          {
            facebookPublish: {
              lastPublishedAt: new Date().toISOString(),
              permalink: result.permalink,
              postId: result.postId,
              contentHash: '',
            },
          },
          { userEdit: false }
        )
      } else {
        setFbPublishError(result.error || 'Publishing failed')
      }
    } catch (error) {
      const message = extractApiError(error)
      setFbPublishError(message)

      errorReportingService.captureException(error instanceof Error ? error : message, {
        feature: 'facebook_publish',
        creativeId: creative.id,
      })
    } finally {
      setFbPublishing(false)
    }
  }, [creative.id, updateCreativeContent])

  const fbPlatform = usePlatformPublish({
    provider: 'facebook',
    creativeId: creative.id,
    publishFn: handlePublishToFacebook,
    beforeStatusCheck: useCallback(() => {
      if (!gate('publish_facebook')) return false
      const fbTappedShape:
        (typeof SocialPublishTappedShape)[keyof typeof SocialPublishTappedShape] | undefined =
        (fbContent?.mediaItems?.length ?? 0) >= 2
          ? SocialPublishTappedShape.CAROUSEL
          : fbContent?.videoAttachmentId
            ? SocialPublishTappedShape.SHORT_VERTICAL_VIDEO
            : fbContent?.imageAttachmentId
              ? SocialPublishTappedShape.STATIC_IMAGE
              : undefined
      track(AnalyticsEvents.Social.SOCIAL_PUBLISH_TAPPED, {
        platform: 'facebook',
        creative_id: creative.id,
        shape: fbTappedShape,
        source: 'composer',
      })
      setFbPublishError(null)
      return true
    }, [gate, track, creative.id, fbContent]),
    isConnected: useCallback(
      (status: { isConnected: boolean; config?: { pageId?: string } }) =>
        status.isConnected && !!status.config?.pageId,
      []
    ),
    providerDisplayName: 'Facebook',
  })
  const handleFbPostClick = fbPlatform.handlePostClick

  // ─── LinkedIn Publish State ─────────────────────────────────────────
  const isLinkedInPost = creative.type === CreativeType.LinkedInPost
  const liContent = isLinkedInPost ? (creative.content as LinkedInPostContent) : null
  const liPublishMeta = liContent?.linkedinPublish ?? null

  const [liPublishing, setLiPublishing] = useState(false)
  const [liPublishError, setLiPublishError] = useState<string | null>(null)

  const isLiveOnLinkedIn = !!liPublishMeta?.lastPublishedAt

  const handlePublishToLinkedIn = useCallback(async () => {
    setLiPublishing(true)
    setLiPublishError(null)

    try {
      // Off its future slot first (see takeOffScheduleForManualPublish); the
      // server refuses a manual publish of a still-scheduled post.
      if (!(await takeOffScheduleForManualPublish(creative.id))) return
      const result = await CreativeService.creativeControllerPublishToLinkedInV1(creative.id)
      if (result.success) {
        updateCreativeContent(
          creative.id,
          {
            linkedinPublish: {
              lastPublishedAt: new Date().toISOString(),
              permalink: result.permalink,
              postUrn: result.postUrn,
            },
          },
          { userEdit: false }
        )
      } else {
        setLiPublishError(result.error || 'Publishing failed')
      }
    } catch (error) {
      const message = extractApiError(error)
      setLiPublishError(message)

      errorReportingService.captureException(error instanceof Error ? error : message, {
        feature: 'linkedin_publish',
        creativeId: creative.id,
      })
    } finally {
      setLiPublishing(false)
    }
  }, [creative.id, updateCreativeContent])

  const liPlatform = usePlatformPublish({
    provider: 'linkedin',
    creativeId: creative.id,
    publishFn: handlePublishToLinkedIn,
    beforeStatusCheck: useCallback(() => {
      if (!gate('publish_linkedin')) return false
      const liTappedShape:
        (typeof SocialPublishTappedShape)[keyof typeof SocialPublishTappedShape] | undefined =
        liContent?.videoAttachmentId
          ? SocialPublishTappedShape.SHORT_VERTICAL_VIDEO
          : liContent?.imageAttachmentId
            ? SocialPublishTappedShape.STATIC_IMAGE
            : undefined
      track(AnalyticsEvents.Social.SOCIAL_PUBLISH_TAPPED, {
        platform: 'linkedin',
        creative_id: creative.id,
        shape: liTappedShape,
        source: 'composer',
      })
      setLiPublishError(null)
      return true
    }, [gate, track, creative.id, liContent]),
    providerDisplayName: 'LinkedIn',
  })
  const handleLiPostClick = liPlatform.handlePostClick

  // ─── X Publish State ──────────────────────────────────────────────────
  const isXPost = creative.type === CreativeType.XPost
  const xContent = isXPost ? (creative.content as XPostContent) : null
  const xPublishMeta = xContent?.xPublish ?? null

  const xPublishStatus = usePublishStatusStore(s => s.statuses[creative.id])
  const setXPublishing = usePublishStatusStore(s => s.setPublishing)
  const clearXPublishStatus = usePublishStatusStore(s => s.clearStatus)
  const isXPublishing = isXPost && xPublishStatus?.status === 'publishing'
  const xPublishError =
    isXPost && xPublishStatus?.status === 'failed'
      ? (xPublishStatus.error ?? 'Publishing failed')
      : null

  const isLiveOnX = !!xPublishMeta?.lastPublishedAt

  // Clear error after 10 seconds
  useEffect(() => {
    if (xPublishError) {
      const timer = setTimeout(() => clearXPublishStatus(creative.id), 10_000)
      return () => clearTimeout(timer)
    }
  }, [xPublishError, creative.id, clearXPublishStatus])

  const handlePublishToX = useCallback(async () => {
    setXPublishing(creative.id, 'x')

    try {
      // Off its future slot first (see takeOffScheduleForManualPublish); the
      // server refuses a manual publish of a still-scheduled post.
      if (!(await takeOffScheduleForManualPublish(creative.id))) return
      await CreativeService.creativeControllerPublishToXV1(creative.id)
      // Server returns 202 immediately — Pusher will notify on completion
    } catch (error) {
      const message = extractApiError(error)
      usePublishStatusStore.getState().setComplete(creative.id, {
        platform: 'x',
        success: false,
        error: message,
        retryable: true,
      })
      errorReportingService.captureException(error instanceof Error ? error : message, {
        feature: 'x_publish',
        creativeId: creative.id,
      })
    }
  }, [creative.id, setXPublishing])

  const xPlatform = usePlatformPublish({
    provider: 'twitter',
    creativeId: creative.id,
    publishFn: handlePublishToX,
    beforeStatusCheck: useCallback(() => {
      if (!isRolloutOn('x')) return false
      if (!gate('publish_twitter')) return false
      track(AnalyticsEvents.Social.SOCIAL_PUBLISH_TAPPED, {
        platform: 'twitter',
        creative_id: creative.id,
        shape: xContent?.imageAttachmentId ? SocialPublishTappedShape.STATIC_IMAGE : undefined,
        source: 'composer',
      })
      clearXPublishStatus(creative.id)
      return true
    }, [isRolloutOn, gate, track, clearXPublishStatus, creative.id, xContent]),
    providerDisplayName: 'X',
  })
  const handleXPostClick = xPlatform.handlePostClick

  // ─── Pinterest Publish State ──────────────────────────────────────────
  // Pinterest publish is async — server returns 202 and Pusher fires the real
  // outcome (see pinterest-publish.service.ts:55). State lives in the shared
  // publish-status store so ChatPusherHandler can flip success/failure on us;
  // the previous local-state version treated 202 as `result.success === false`
  // and flashed "Publishing failed" on every successful click.
  const isPinterestPin = creative.type === CreativeType.PinterestPin
  const pinContent = isPinterestPin ? (creative.content as PinterestPinContent) : null
  const pinPublishMeta = pinContent?.pinterestPublish ?? null

  const pinterestPublishStatus = usePublishStatusStore(s => s.statuses[creative.id])
  const setPinterestPublishing = usePublishStatusStore(s => s.setPublishing)
  const clearPinterestPublishStatus = usePublishStatusStore(s => s.clearStatus)
  const pinPublishing = isPinterestPin && pinterestPublishStatus?.status === 'publishing'
  const pinPublishError =
    isPinterestPin && pinterestPublishStatus?.status === 'failed'
      ? (pinterestPublishStatus.error ?? 'Publishing failed')
      : null

  const isLiveOnPinterest = !!pinPublishMeta?.lastPublishedAt

  useEffect(() => {
    if (pinPublishError) {
      const timer = setTimeout(() => clearPinterestPublishStatus(creative.id), 10_000)
      return () => clearTimeout(timer)
    }
  }, [pinPublishError, creative.id, clearPinterestPublishStatus])

  const handlePublishToPinterest = useCallback(async () => {
    setPinterestPublishing(creative.id, 'pinterest')

    try {
      // Off its future slot first (see takeOffScheduleForManualPublish); the
      // server refuses a manual publish of a still-scheduled post.
      if (!(await takeOffScheduleForManualPublish(creative.id))) return
      await CreativeService.creativeControllerPublishToPinterestV1(creative.id)
      // Server returns 202 — Pusher will land the real outcome via
      // ChatPusherHandler, which writes pinterestPublish metadata + flips the
      // store status to 'success' / 'failed'.
    } catch (error) {
      // Sync rejection (e.g. 4xx auth/validation). Real errors only — a 202
      // never reaches this branch.
      const message = extractApiError(error)
      usePublishStatusStore.getState().setComplete(creative.id, {
        platform: 'pinterest',
        success: false,
        error: message,
        retryable: true,
      })
      errorReportingService.captureException(error instanceof Error ? error : message, {
        feature: 'pinterest_publish',
        creativeId: creative.id,
      })
    }
  }, [creative.id, setPinterestPublishing])

  const pinPlatform = usePlatformPublish({
    provider: 'pinterest',
    creativeId: creative.id,
    publishFn: handlePublishToPinterest,
    beforeStatusCheck: useCallback(() => {
      if (!gate('publish_pinterest')) return false
      const pinTappedShape:
        (typeof SocialPublishTappedShape)[keyof typeof SocialPublishTappedShape] | undefined =
        pinContent?.videoAttachmentId
          ? SocialPublishTappedShape.SHORT_VERTICAL_VIDEO
          : pinContent?.imageAttachmentId
            ? SocialPublishTappedShape.STATIC_IMAGE
            : undefined
      track(AnalyticsEvents.Social.SOCIAL_PUBLISH_TAPPED, {
        platform: 'pinterest',
        creative_id: creative.id,
        shape: pinTappedShape,
        source: 'composer',
      })
      clearPinterestPublishStatus(creative.id)
      return true
    }, [gate, track, creative.id, clearPinterestPublishStatus, pinContent]),
    isConnected: useCallback(
      (status: { isConnected: boolean; config?: { defaultBoardId?: string } }) =>
        status.isConnected && !!status.config?.defaultBoardId,
      []
    ),
    providerDisplayName: 'Pinterest',
  })
  const handlePinPostClick = pinPlatform.handlePostClick

  // ─── Image Download State ─────────────────────────────────────────────
  const [isDownloading, setIsDownloading] = useState(false)

  const imageUrl =
    'imageUrl' in creative.content ? (creative.content.imageUrl as string | null) : null

  const imageAttachmentId =
    'imageAttachmentId' in creative.content
      ? (creative.content.imageAttachmentId as string | null | undefined)
      : null

  const videoUrl =
    'videoUrl' in creative.content ? (creative.content.videoUrl as string | null) : null

  const videoAttachmentId =
    'videoAttachmentId' in creative.content
      ? (creative.content.videoAttachmentId as string | null | undefined)
      : null

  const hasMedia = !!(imageUrl || imageAttachmentId || videoUrl || videoAttachmentId)

  // ─── Scheduling State ──────────────────────────────────────────────────
  const isYouTubeShort = creative.type === CreativeType.YouTubeShort
  const ytContent = isYouTubeShort ? (creative.content as YouTubeShortsContent) : null
  const ytHasVideo = !!(ytContent?.videoUrl || ytContent?.videoAttachmentId)

  const isSocialPost =
    isInstagramPost ||
    isFacebookPost ||
    isTikTokVideo ||
    isLinkedInPost ||
    isYouTubeShort ||
    isPinterestPin
  // Scheduling is a POST-level concept: every platform of a post shares one
  // schedule date. The date is physically stored per-platform creative, but the
  // post-level truth lives on `idea.scheduledAt` (computed on load from the
  // earliest future per-creative schedule, and set on confirm). Drive the
  // Schedule CTA + date pill + reschedule label off that so the state is
  // consistent across every platform tab — otherwise a post scheduled on a
  // non-primary platform (e.g. IG removed, FB added) reads "Schedule" when the
  // composer opens on the primary tab whose own creative.scheduledAt is null.
  // Standalone (non-Post) mode keeps the legacy per-creative value.
  const postScheduledAt =
    postContext && postContext.idea.status === 'scheduled'
      ? postContext.idea.scheduledAt
      : undefined
  const scheduledAt = (postScheduledAt ?? creative.scheduledAt) as string | null | undefined
  const isScheduledInFuture = useMemo(() => {
    if (!scheduledAt) return false
    const scheduled = new Date(scheduledAt)
    const now = new Date()
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    return scheduled >= todayStart
  }, [scheduledAt])

  // Schedule dialog defaults. Prefers committed `scheduledAt`; falls back to
  // `stagedScheduledAt` from the nav payload (e.g. PostModal's editors) so
  // the user lands in the dialog with the time they already picked.
  const scheduledDateParts = useMemo(() => {
    const source = scheduledAt ?? stagedScheduledAt
    if (!source) return null
    // Seed the picker with the brand-TZ wall clock (was UTC, so a 06:00Z post
    // opened the modal on "6:00 AM" while the CTA read "9:00 AM"). scheduleTz is
    // the same zone ScheduleDialog interprets the picked time in.
    return splitIsoInTz(source, scheduleTz)
  }, [scheduledAt, stagedScheduledAt, scheduleTz])

  // Standalone (non-Post) scheduling only. Post mode is owned by PostModal
  // now — it runs the connect gate (via onConnectRequired) and calls
  // confirmIdea itself, so ScheduleDialog never opens in Post mode.
  const handleSchedule = useCallback(
    async (newScheduledAt: string) => {
      // The schedule call can be REFUSED (platform content limits, already
      // published). Report it, then RETHROW: ScheduleDialog treats a resolved
      // `onSchedule` as success, so swallowing the error here made it flash a
      // green "Scheduled!" and auto-close over a post the server had just
      // refused. Rejecting is the dialog's failure contract — it drops back to
      // idle, stays open, and renders the reason inline.
      try {
        await useCalendarStore.getState().scheduleCreative(creative.id, newScheduledAt)
      } catch (err) {
        errorReportingService.captureException(
          err instanceof Error ? err : new Error(String(err)),
          {
            feature: 'composer_schedule',
            creativeId: creative.id,
          }
        )
        throw err
      }
      const standalonePlatform = creativeTypeToPlatform(creative.type)
      const standaloneLeadTime = Math.max(
        0,
        Math.round((new Date(newScheduledAt).getTime() - Date.now()) / 60_000)
      )
      track(AnalyticsEvents.Posts.POST_SCHEDULED, {
        source: 'composer',
        creative_id: creative.id,
        autopilot_id: creative.autopilotId ?? undefined,
        platforms: standalonePlatform ? [standalonePlatform] : [],
        platform_count: standalonePlatform ? 1 : 0,
        scheduled_at: newScheduledAt,
        lead_time_minutes: standaloneLeadTime,
        is_immediate: standaloneLeadTime < 2,
        entry_source: journeyEntrySource,
        via_composer: true,
      })
      updateCreative(creative.id, { scheduledAt: newScheduledAt })
      setShowScheduleDialog(false)
    },
    [creative.id, creative.type, creative.autopilotId, updateCreative, track, journeyEntrySource]
  )

  const handleUnschedule = useCallback(async () => {
    // Shared by the schedule dialog and the direct ••• menu item. Clears the
    // schedule (server emits POST_UNSCHEDULED) so the post reverts to a draft,
    // still listed under /posts. Reversible — no confirm dialog.
    try {
      if (postContext) {
        // Post mode: scheduling is post-level and the CTA reads
        // `postContext.idea` (reactive from the idea store). Route through the
        // idea store so it unschedules the primary creative, flips the idea to a
        // draft (CTA reverts to "Schedule"), and keeps home + cockpit in sync.
        await unschedulePostIdea(postContext.idea.projectId)
      } else {
        // Standalone (per-creative) mode.
        await useCalendarStore.getState().unscheduleCreative(creative.id)
      }
      // Always clear the active creative's own `scheduledAt`: the Schedule CTA +
      // "Scheduled for …" pill fall back to `creative.scheduledAt` once the
      // post-level idea is no longer 'scheduled', so without this the pill (and
      // the Unschedule menu item) stay up in post mode.
      updateCreative(creative.id, { scheduledAt: null })
      setShowScheduleDialog(false)
      toast.success('Moved to draft. Find it in Posts.')
    } catch (err) {
      errorReportingService.captureException(err instanceof Error ? err : new Error(String(err)), {
        feature: 'composer_unschedule',
        creativeId: creative.id,
      })
      toast.error("Couldn't move this post to draft. Try again.")
    }
  }, [creative.id, updateCreative, postContext, unschedulePostIdea])

  const handleDownload = useCallback(async () => {
    if (!gate('download')) return

    const media = collectDownloadableMedia(creative.content)
    if (media.length === 0) return

    // Tracked here (not per button) so every entry point reports once, with
    // the format derived from what actually downloads (carousels included).
    track(AnalyticsEvents.Composer.COMPOSER_DOWNLOAD_TAPPED, {
      creative_id: creative.id,
      creative_type: creative.type,
      format: media.some(m => m.kind === 'video') ? 'video' : 'image',
    })

    const suffix = CREATOR_MODE_MAP[creative.type]

    setIsDownloading(true)
    try {
      // A hung signed-URL fetch used to spin forever with no feedback (monkey:
      // 45s, nothing). The race caps the wait; the toast + unlocked button are
      // the feedback, and a retry gets a fresh signed URL.
      await Promise.race([
        downloadCreativeMedia(`${creative.name}_${suffix}`, media),
        new Promise((_, reject) => setTimeout(() => reject(new Error('download-timeout')), 30_000)),
      ])
    } catch (error) {
      if (error instanceof Error && error.message === 'download-timeout') {
        toast.error('The download is taking too long. Try again.')
      } else {
        console.error('Download failed:', error)
        toast.error("Couldn't download the media. Try again.")
      }
    } finally {
      setIsDownloading(false)
    }
  }, [creative, gate, track])

  // ─── Instagram Copy State ──────────────────────────────────────────────
  const [igCopiedField, setIgCopiedField] = useState<'caption' | 'hashtags' | null>(null)
  const [, setIgCopyFailed] = useState(false)
  const igCopiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [fbCopied, setFbCopied] = useState(false)
  const [, setFbCopyFailed] = useState(false)
  const fbCopiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleIgCopy = useCallback(
    async (text: string, field: 'caption' | 'hashtags') => {
      if (!text) return
      setIgCopyFailed(false)
      try {
        await navigator.clipboard.writeText(text)
        setIgCopiedField(field)
        if (igCopiedTimerRef.current) clearTimeout(igCopiedTimerRef.current)
        igCopiedTimerRef.current = setTimeout(() => setIgCopiedField(null), 2000)
        track(AnalyticsEvents.Composer.COMPOSER_PREVIEW_CONTENT_COPIED, {
          creative_id: creative.id,
          creative_type: 'instagram_post',
          field,
        })
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Clipboard unavailable'
        console.warn('Instagram copy failed:', message, error)
        setIgCopyFailed(true)
        if (igCopiedTimerRef.current) clearTimeout(igCopiedTimerRef.current)
        igCopiedTimerRef.current = setTimeout(() => setIgCopyFailed(false), 2000)
      }
    },
    [creative.id, track]
  )

  const handleFbCopy = useCallback(async () => {
    const text = fbContent?.message
    if (!text) return
    setFbCopyFailed(false)
    try {
      await navigator.clipboard.writeText(text)
      setFbCopied(true)
      if (fbCopiedTimerRef.current) clearTimeout(fbCopiedTimerRef.current)
      fbCopiedTimerRef.current = setTimeout(() => setFbCopied(false), 2000)
      track(AnalyticsEvents.Composer.COMPOSER_PREVIEW_CONTENT_COPIED, {
        creative_id: creative.id,
        creative_type: 'facebook_post',
        field: 'message',
      })
    } catch (error) {
      console.warn('Facebook copy failed:', error)
      setFbCopyFailed(true)
      if (fbCopiedTimerRef.current) clearTimeout(fbCopiedTimerRef.current)
      fbCopiedTimerRef.current = setTimeout(() => setFbCopyFailed(false), 2000)
    }
  }, [fbContent?.message, creative.id, track])

  // ─── LinkedIn Copy State ──────────────────────────────────────────────
  const [liCopiedField, setLiCopiedField] = useState<'caption' | 'hashtags' | null>(null)
  const [, setLiCopyFailed] = useState(false)
  const liCopiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (igCopiedTimerRef.current) clearTimeout(igCopiedTimerRef.current)
      if (fbCopiedTimerRef.current) clearTimeout(fbCopiedTimerRef.current)
      if (liCopiedTimerRef.current) clearTimeout(liCopiedTimerRef.current)
    }
  }, [])

  const handleLiCopy = useCallback(
    async (text: string, field: 'caption' | 'hashtags') => {
      if (!text) return
      setLiCopyFailed(false)
      try {
        await navigator.clipboard.writeText(text)
        setLiCopiedField(field)
        if (liCopiedTimerRef.current) clearTimeout(liCopiedTimerRef.current)
        liCopiedTimerRef.current = setTimeout(() => setLiCopiedField(null), 2000)
        track(AnalyticsEvents.Composer.COMPOSER_PREVIEW_CONTENT_COPIED, {
          creative_id: creative.id,
          creative_type: 'linkedin_post',
          field,
        })
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Clipboard unavailable'
        console.warn('LinkedIn copy failed:', message, error)
        setLiCopyFailed(true)
        if (liCopiedTimerRef.current) clearTimeout(liCopiedTimerRef.current)
        liCopiedTimerRef.current = setTimeout(() => setLiCopyFailed(false), 2000)
      }
    },
    [creative.id, track]
  )

  // ─── Mobile Primary Action (capsule icon) ─────────────────────────────
  const mobilePrimaryAction = useMemo(() => {
    if (!isMobile) return null

    const iconBtn = 'p-2 text-paper-dim transition-colors hover:text-paper'

    if (isInstagramPost && (imageUrl || imageAttachmentId)) {
      if (igPublishMeta?.lastPublishedAt) {
        return (
          <a
            href={igPublishMeta.permalink}
            target="_blank"
            rel="noopener noreferrer"
            className={`${iconBtn} text-green-600 hover:text-green-700`}
            aria-label="Live on Instagram"
          >
            <Check size={18} />
          </a>
        )
      }
      if (isScheduledInFuture) {
        return (
          <button onClick={handleScheduleClick} className={iconBtn} aria-label="Scheduled">
            <Calendar size={18} />
          </button>
        )
      }
      return (
        <button
          onClick={() => setShowPostConfirm(true)}
          disabled={isPublishing}
          className={`${iconBtn} disabled:opacity-50`}
          aria-label="Post to Instagram"
        >
          {isPublishing ? <Loader2 size={18} className="animate-spin" /> : <Instagram size={18} />}
        </button>
      )
    }

    if (isFacebookPost && hasMedia) {
      if (isLiveOnFacebook) {
        return (
          <a
            href={fbPublishMeta!.permalink}
            target="_blank"
            rel="noopener noreferrer"
            className={`${iconBtn} text-green-600 hover:text-green-700`}
            aria-label="Live on Facebook"
          >
            <Check size={18} />
          </a>
        )
      }
      if (isScheduledInFuture) {
        return (
          <button onClick={handleScheduleClick} className={iconBtn} aria-label="Scheduled">
            <Calendar size={18} />
          </button>
        )
      }
      // Download used to live here as the FB fallback action; the universal
      // header Download button owns it now (one icon, every platform).
      return null
    }

    if (isLinkedInPost) {
      if (isLiveOnLinkedIn) {
        return (
          <a
            href={liPublishMeta!.permalink}
            target="_blank"
            rel="noopener noreferrer"
            className={`${iconBtn} text-green-600 hover:text-green-700`}
            aria-label="Live on LinkedIn"
          >
            <Check size={18} />
          </a>
        )
      }
      if (isScheduledInFuture) {
        return (
          <button onClick={handleScheduleClick} className={iconBtn} aria-label="Scheduled">
            <Calendar size={18} />
          </button>
        )
      }
      return (
        <button
          onClick={handleLiPostClick}
          disabled={liPublishing}
          className={`${iconBtn} disabled:opacity-50`}
          aria-label="Post to LinkedIn"
        >
          {liPublishing ? <Loader2 size={18} className="animate-spin" /> : <Linkedin size={18} />}
        </button>
      )
    }

    if (isXPost) {
      if (isLiveOnX) {
        return (
          <span className={`${iconBtn} text-green-600`}>
            <Check size={18} />
          </span>
        )
      }
      return (
        <button
          onClick={handleXPostClick}
          disabled={isXPublishing}
          className={`${iconBtn} disabled:opacity-50`}
          aria-label="Post to X"
        >
          {isXPublishing ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        </button>
      )
    }

    if (
      !isInstagramPost &&
      !isFacebookPost &&
      !isLinkedInPost &&
      !isXPost &&
      (imageUrl || imageAttachmentId)
    ) {
      return (
        <button
          onClick={() => {
            track(AnalyticsEvents.Composer.COMPOSER_DOWNLOAD_TAPPED, {
              creative_id: creative.id,
              creative_type: creative.type,
              format: 'image',
            })
            handleDownload()
          }}
          disabled={isDownloading}
          className={`${iconBtn} disabled:opacity-50`}
          aria-label="Download"
        >
          {isDownloading ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
        </button>
      )
    }

    return null
  }, [
    isMobile,
    isInstagramPost,
    isFacebookPost,
    isLinkedInPost,
    imageUrl,
    imageAttachmentId,
    hasMedia,
    isScheduledInFuture,
    igPublishMeta,
    isLiveOnFacebook,
    fbPublishMeta,
    isLiveOnLinkedIn,
    liPublishMeta,
    liPublishing,
    handleLiPostClick,
    isPublishing,
    isDownloading,
    handleDownload,
    track,
    creative.id,
    creative.type,
  ])

  return (
    <div className="relative flex items-center gap-3 border-b border-line bg-bg-2 px-4 py-2.5 text-paper">
      {/* Back */}
      <button
        onClick={() => {
          track(AnalyticsEvents.Composer.COMPOSER_BACK_TAPPED, {
            creative_id: creative.id,
            creative_type: creative.type,
          })
          navigate(-1)
        }}
        className="rounded-[8px] p-1.5 text-paper-dim transition-colors hover:bg-bg-3 hover:text-paper"
        aria-label="Go back"
      >
        <ArrowLeft size={18} />
      </button>

      {/* Type badge — hidden in Post mode (the platform tab strip already
          identifies the active platform). */}
      {!postContext && <CreativeTypeBadge type={creative.type} />}

      {/* Name — editable creative name in both Post and standalone modes.
          In Post mode, each sibling creative keeps its own name; the
          composer header shows the active sibling's name. */}
      {isEditingName ? (
        <input
          ref={nameInputRef}
          value={editName}
          onChange={e => setEditName(e.target.value)}
          onBlur={handleSaveName}
          onKeyDown={handleNameKeyDown}
          className="min-w-0 flex-1 rounded-[8px] border border-acid bg-bg-3 px-2 py-1 text-[13px] font-medium text-paper outline-none"
        />
      ) : (
        <button
          onClick={() => {
            setEditName(creative.name)
            setIsEditingName(true)
          }}
          className="min-w-0 flex-1 truncate text-left text-[13px] font-medium text-paper hover:text-acid"
          title="Click to rename"
        >
          {creative.name}
        </button>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Download — first-class export for any post with media, desktop AND
          mobile (was buried in a legacy mobile-Facebook branch; repeated user
          request, support round 15 / Irv). Same subscription gate as publish
          (trials count) via handleDownload's gate('download'). Gated on the
          ACTUAL downloadable set (not hasMedia): carousels carry their media
          in mediaItems, which the top-level hasMedia fields only see when the
          first slide happens to be mirrored into imageUrl. */}
      {collectDownloadableMedia(creative.content).length > 0 && (
        <button
          onClick={() => void handleDownload()}
          disabled={isDownloading}
          className="rounded-[8px] p-1.5 text-paper-dim transition-colors hover:bg-bg-3 hover:text-paper disabled:opacity-50"
          aria-label="Download media"
          title="Download media"
          data-testid="composer-download"
        >
          {isDownloading ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
        </button>
      )}

      {/* Instagram Post — desktop only */}
      {isInstagramPost && !isMobile && hasMedia && (
        <div className="relative">
          {igPublishMeta?.lastPublishedAt ? (
            <a
              href={igPublishMeta.permalink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-[10px] border border-green-200 bg-green-50 px-4 py-2 text-sm font-medium text-green-700 transition-colors hover:bg-green-100"
            >
              <Check size={14} />
              Live on Instagram
            </a>
          ) : (
            <div className="flex items-center gap-2">
              <ScheduleSplitButton
                scheduledAt={isScheduledInFuture ? scheduledAt : null}
                needsApproval={needsApproval}
                onSchedule={handleScheduleClick}
                onPostNow={handlePostNow}
                postContext={!!postContext}
                justScheduled={justScheduled}
                disabled={isActiveCreativePublished}
                livePermalink={activeLivePermalink}
              />
              {!postContext && (
                <Button
                  variant="t3-primary"
                  size="t3-sm"
                  onClick={handlePostClick}
                  disabled={isPublishing}
                >
                  {isPublishing ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Send size={14} />
                  )}
                  {isPublishing ? 'Posting...' : 'Post Now'}
                </Button>
              )}
            </div>
          )}
          {publishError && (
            <div className="absolute top-full right-0 z-20 mt-1 w-64 rounded-[10px] border border-line-bright bg-bg-3 p-3 shadow-[var(--shadow-card)]">
              <p className="text-[12px] text-[color:var(--alert-error-text)]">{publishError}</p>
              <button
                onClick={handlePostClick}
                className="mt-2 w-full rounded-[6px] border border-line-bright bg-bg-2 px-2 py-1.5 text-[12px] font-medium text-paper transition-colors hover:bg-bg-3"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      )}

      {/* TikTok Video — desktop only */}
      {isTikTokVideo && !isMobile && tiktokHasVideo && (
        <div className="relative">
          {tiktokPublishSuccess ? (
            <Button size="sm" disabled>
              <Check size={14} />
              Published!
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <ScheduleSplitButton
                scheduledAt={isScheduledInFuture ? scheduledAt : null}
                needsApproval={needsApproval}
                onSchedule={handleScheduleClick}
                onPostNow={handlePostNow}
                postContext={!!postContext}
                justScheduled={justScheduled}
                disabled={isActiveCreativePublished}
                livePermalink={activeLivePermalink}
              />
              {!postContext && (
                <Button
                  variant="t3-primary"
                  size="t3-sm"
                  onClick={handleTikTokPostClick}
                  disabled={isTikTokPublishing}
                >
                  {isTikTokPublishing ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Send size={14} />
                  )}
                  {isTikTokPublishing ? 'Publishing...' : 'Post Now'}
                </Button>
              )}
            </div>
          )}
          {tikTokPublishError && (
            <div className="absolute top-full right-0 z-20 mt-1 w-64 rounded-[10px] border border-line-bright bg-bg-3 p-3 shadow-[var(--shadow-card)]">
              <p className="text-[12px] text-[color:var(--alert-error-text)]">
                {tikTokPublishError}
              </p>
              <button
                onClick={handleTikTokPostClick}
                className="mt-2 w-full rounded-[6px] border border-line-bright bg-bg-2 px-2 py-1.5 text-[12px] font-medium text-paper transition-colors hover:bg-bg-3"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      )}

      {/* YouTube Short — desktop only */}
      {isYouTubeShort && !isMobile && ytHasVideo && (
        <div className="relative">
          {youtubePublishSuccess ? (
            <Button size="sm" disabled>
              <Check size={14} />
              Published!
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <ScheduleSplitButton
                scheduledAt={isScheduledInFuture ? scheduledAt : null}
                needsApproval={needsApproval}
                onSchedule={handleScheduleClick}
                onPostNow={handlePostNow}
                postContext={!!postContext}
                justScheduled={justScheduled}
                disabled={isActiveCreativePublished}
                livePermalink={activeLivePermalink}
              />
              {!postContext && (
                <Button
                  variant="t3-primary"
                  size="t3-sm"
                  onClick={handleYouTubePostClick}
                  disabled={isYouTubePublishing}
                >
                  {isYouTubePublishing ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Send size={14} />
                  )}
                  {isYouTubePublishing ? 'Publishing...' : 'Post Now'}
                </Button>
              )}
            </div>
          )}
          {youTubePublishError && (
            <div className="absolute top-full right-0 z-20 mt-1 w-64 rounded-[10px] border border-line-bright bg-bg-3 p-3 shadow-[var(--shadow-card)]">
              <p className="text-[12px] text-[color:var(--alert-error-text)]">
                {youTubePublishError}
              </p>
              <button
                onClick={handleYouTubePostClick}
                className="mt-2 w-full rounded-[6px] border border-line-bright bg-bg-2 px-2 py-1.5 text-[12px] font-medium text-paper transition-colors hover:bg-bg-3"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      )}

      {/* Facebook Post — desktop only */}
      {isFacebookPost && !isMobile && (
        <div className="relative">
          {isLiveOnFacebook ? (
            <a
              href={fbPublishMeta!.permalink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-[10px] border border-green-200 bg-green-50 px-4 py-2 text-sm font-medium text-green-700 transition-colors hover:bg-green-100"
            >
              <Check size={14} />
              Live on Facebook
            </a>
          ) : (
            <div className="flex items-center gap-2">
              <ScheduleSplitButton
                scheduledAt={isScheduledInFuture ? scheduledAt : null}
                needsApproval={needsApproval}
                onSchedule={handleScheduleClick}
                onPostNow={handlePostNow}
                postContext={!!postContext}
                justScheduled={justScheduled}
                disabled={isActiveCreativePublished}
                livePermalink={activeLivePermalink}
              />
              {!postContext && (
                <Button
                  variant="t3-primary"
                  size="t3-sm"
                  onClick={handleFbPostClick}
                  disabled={fbPublishing}
                >
                  {fbPublishing ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Send size={14} />
                  )}
                  {fbPublishing ? 'Posting...' : 'Post Now'}
                </Button>
              )}
            </div>
          )}
          {fbPublishError && (
            <div className="absolute top-full right-0 z-20 mt-1 w-64 rounded-[10px] border border-line-bright bg-bg-3 p-3 shadow-[var(--shadow-card)]">
              <p className="text-[12px] text-[color:var(--alert-error-text)]">{fbPublishError}</p>
              <button
                onClick={handleFbPostClick}
                className="mt-2 w-full rounded-[6px] border border-line-bright bg-bg-2 px-2 py-1.5 text-[12px] font-medium text-paper transition-colors hover:bg-bg-3"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      )}

      {/* LinkedIn Post — desktop only */}
      {isLinkedInPost && !isMobile && (
        <div className="relative">
          {isLiveOnLinkedIn ? (
            <a
              href={liPublishMeta!.permalink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-[10px] border border-green-200 bg-green-50 px-4 py-2 text-sm font-medium text-green-700 transition-colors hover:bg-green-100"
            >
              <Check size={14} />
              Live on LinkedIn
            </a>
          ) : (
            <div className="flex items-center gap-2">
              <ScheduleSplitButton
                scheduledAt={isScheduledInFuture ? scheduledAt : null}
                needsApproval={needsApproval}
                onSchedule={handleScheduleClick}
                onPostNow={handlePostNow}
                postContext={!!postContext}
                justScheduled={justScheduled}
                disabled={isActiveCreativePublished}
                livePermalink={activeLivePermalink}
              />
              {!postContext && (
                <Button
                  variant="t3-primary"
                  size="t3-sm"
                  onClick={handleLiPostClick}
                  disabled={liPublishing}
                >
                  {liPublishing ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Send size={14} />
                  )}
                  {liPublishing ? 'Posting...' : 'Post Now'}
                </Button>
              )}
            </div>
          )}
          {liPublishError && (
            <div className="absolute top-full right-0 z-20 mt-1 w-64 rounded-[10px] border border-line-bright bg-bg-3 p-3 shadow-[var(--shadow-card)]">
              <p className="text-[12px] text-[color:var(--alert-error-text)]">{liPublishError}</p>
              <button
                onClick={handleLiPostClick}
                className="mt-2 w-full rounded-[6px] border border-line-bright bg-bg-2 px-2 py-1.5 text-[12px] font-medium text-paper transition-colors hover:bg-bg-3"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      )}

      {/* Pinterest Pin — desktop only */}
      {isPinterestPin && !isMobile && (
        <div className="relative">
          {isLiveOnPinterest ? (
            <a
              href={pinPublishMeta!.permalink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-[10px] border border-green-200 bg-green-50 px-4 py-2 text-sm font-medium text-green-700 transition-colors hover:bg-green-100"
            >
              <Check size={14} />
              Live on Pinterest
            </a>
          ) : (
            <div className="flex items-center gap-2">
              <ScheduleSplitButton
                scheduledAt={isScheduledInFuture ? scheduledAt : null}
                needsApproval={needsApproval}
                onSchedule={handleScheduleClick}
                onPostNow={handlePostNow}
                postContext={!!postContext}
                justScheduled={justScheduled}
                disabled={isActiveCreativePublished}
                livePermalink={activeLivePermalink}
              />
              {!postContext && (
                <Button
                  variant="t3-primary"
                  size="t3-sm"
                  onClick={handlePinPostClick}
                  disabled={pinPublishing}
                >
                  {pinPublishing ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Send size={14} />
                  )}
                  {pinPublishing ? 'Posting...' : 'Post Now'}
                </Button>
              )}
            </div>
          )}
          {pinPublishError && (
            <div className="absolute top-full right-0 z-20 mt-1 w-64 rounded-[10px] border border-line-bright bg-bg-3 p-3 shadow-[var(--shadow-card)]">
              <p className="text-[12px] text-[color:var(--alert-error-text)]">{pinPublishError}</p>
              <button
                onClick={handlePinPostClick}
                className="mt-2 w-full rounded-[6px] border border-line-bright bg-bg-2 px-2 py-1.5 text-[12px] font-medium text-paper transition-colors hover:bg-bg-3"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      )}

      {/* X Post: Copy + Download + Post Now — desktop only, hidden in Post mode */}
      {isXPost && !isMobile && !postContext && (
        <>
          {hasMedia && (
            <Button
              variant="t3-tertiary"
              size="sm"
              onClick={() => {
                track(AnalyticsEvents.Composer.COMPOSER_DOWNLOAD_TAPPED, {
                  creative_id: creative.id,
                  creative_type: creative.type,
                  format: 'image',
                })
                handleDownload()
              }}
              disabled={isDownloading}
            >
              <Download size={14} />
              {isDownloading ? 'Downloading...' : 'Download'}
            </Button>
          )}

          <div className="relative">
            {isLiveOnX ? (
              <span className="inline-flex items-center gap-1.5 rounded-[10px] border border-green-200 bg-green-50 px-4 py-2 text-sm font-medium text-green-700">
                <Check size={14} />
                Posted to X
              </span>
            ) : (
              <Button size="sm" onClick={handleXPostClick} disabled={isXPublishing}>
                {isXPublishing ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Send size={14} />
                )}
                {isXPublishing ? 'Posting...' : 'Post Now'}
              </Button>
            )}
            {xPublishError && (
              <div className="absolute top-full right-0 z-20 mt-1 w-64 rounded-lg border border-red-100 bg-white p-3 shadow-lg">
                <p className="text-xs text-red-500">{xPublishError}</p>
                <button
                  onClick={handleXPostClick}
                  className="mt-2 w-full rounded-md bg-gray-100 px-2 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200"
                >
                  Try Again
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Download button (for non-Instagram image creatives like Logo) — desktop only, hidden in Post mode */}
      {!isInstagramPost &&
        !isFacebookPost &&
        !isLinkedInPost &&
        !isXPost &&
        !isMobile &&
        !postContext &&
        (imageUrl || imageAttachmentId) && (
          <Button
            variant="t3-tertiary"
            size="sm"
            onClick={() => {
              track(AnalyticsEvents.Composer.COMPOSER_DOWNLOAD_TAPPED, {
                creative_id: creative.id,
                creative_type: creative.type,
                format: 'image',
              })
              handleDownload()
            }}
            disabled={isDownloading}
          >
            <Download size={14} />
            {isDownloading ? 'Downloading...' : 'Download'}
          </Button>
        )}

      {/* Actions menu */}
      <div ref={actionsRef} className="relative">
        {isMobile && mobilePrimaryAction ? (
          <div className="flex items-center rounded-full border border-line-bright bg-bg-3">
            {mobilePrimaryAction}
            <div className="h-5 w-px bg-line-bright" />
            <button
              onClick={() => setShowActions(!showActions)}
              className="cursor-pointer p-2 text-paper-dim transition-colors hover:text-paper"
              aria-label="More actions"
            >
              <MoreHorizontal size={18} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowActions(!showActions)}
            className="cursor-pointer rounded-[8px] p-1.5 text-paper-dim transition-colors hover:bg-bg-3 hover:text-paper"
          >
            <MoreHorizontal size={18} />
          </button>
        )}
        {showActions && (
          <div className="absolute right-0 z-20 mt-1 w-48 rounded-[10px] border border-line-bright bg-bg-3 py-1 shadow-[var(--shadow-card)] [&_a]:cursor-pointer [&_button]:cursor-pointer">
            {/* Direct Unschedule — a committed scheduled post drops back to a
                draft (still listed under /posts) without entering the schedule
                dialog. Shown on both mobile and desktop. */}
            {isScheduledInFuture && !needsApproval && (
              <>
                <button
                  onClick={() => {
                    setShowActions(false)
                    void handleUnschedule()
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-[13px] text-paper-dim transition-colors hover:bg-[color:var(--bg-2)] hover:text-[color:var(--paper)]"
                >
                  <CalendarOff size={14} />
                  Unschedule
                </button>
                <div className="my-1 border-t border-line" />
              </>
            )}
            {isMobile && isInstagramPost && hasMedia && !igPublishMeta?.lastPublishedAt && (
              <>
                <button
                  onClick={() => {
                    setShowActions(false)
                    setShowPostConfirm(true)
                  }}
                  disabled={isPublishing}
                  className="flex w-full items-center gap-2 px-3 py-2 text-[13px] font-medium text-paper transition-colors hover:bg-bg-2 disabled:opacity-50"
                >
                  {isPublishing ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Instagram size={14} />
                  )}
                  {isPublishing ? 'Posting...' : 'Post to Instagram'}
                </button>
                <div className="my-1 border-t border-line" />
              </>
            )}
            {isMobile && isInstagramPost && !!igPublishMeta?.lastPublishedAt && (
              <>
                <a
                  href={igPublishMeta.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setShowActions(false)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium text-green-600 hover:bg-green-50"
                >
                  <Check size={14} />
                  Live on Instagram
                </a>
                <div className="my-1 border-t border-line" />
              </>
            )}
            {isMobile && isFacebookPost && hasMedia && !isLiveOnFacebook && (
              <>
                <button
                  onClick={() => {
                    setShowActions(false)
                    handleFbPostClick()
                  }}
                  disabled={fbPublishing}
                  className="flex w-full items-center gap-2 px-3 py-2 text-[13px] font-medium text-paper transition-colors hover:bg-bg-2 disabled:opacity-50"
                >
                  {fbPublishing ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Send size={14} />
                  )}
                  {fbPublishing ? 'Posting...' : 'Post to Facebook'}
                </button>
                <div className="my-1 border-t border-line" />
              </>
            )}
            {isMobile && isFacebookPost && isLiveOnFacebook && (
              <>
                <a
                  href={fbPublishMeta!.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setShowActions(false)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium text-green-600 hover:bg-green-50"
                >
                  <Check size={14} />
                  Live on Facebook
                </a>
                <div className="my-1 border-t border-line" />
              </>
            )}
            {isMobile && isLinkedInPost && !isLiveOnLinkedIn && (
              <>
                <button
                  onClick={() => {
                    setShowActions(false)
                    handleLiPostClick()
                  }}
                  disabled={liPublishing}
                  className="flex w-full items-center gap-2 px-3 py-2 text-[13px] font-medium text-paper transition-colors hover:bg-bg-2 disabled:opacity-50"
                >
                  {liPublishing ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Linkedin size={14} />
                  )}
                  {liPublishing ? 'Posting...' : 'Post to LinkedIn'}
                </button>
                <div className="my-1 border-t border-line" />
              </>
            )}
            {isMobile && isLinkedInPost && isLiveOnLinkedIn && (
              <>
                <a
                  href={liPublishMeta!.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setShowActions(false)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium text-green-600 hover:bg-green-50"
                >
                  <Check size={14} />
                  Live on LinkedIn
                </a>
                <div className="my-1 border-t border-line" />
              </>
            )}
            {isMobile &&
              isSocialPost &&
              !igPublishMeta?.lastPublishedAt &&
              !isLiveOnFacebook &&
              !isLiveOnLinkedIn &&
              !isLiveOnPinterest &&
              !tiktokPublishSuccess &&
              !youtubePublishSuccess && (
                <>
                  <button
                    onClick={() => {
                      setShowActions(false)
                      handleScheduleClick()
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-[13px] text-paper-dim transition-colors hover:bg-bg-2 hover:text-paper"
                  >
                    <Calendar size={14} />
                    {isScheduledInFuture
                      ? `Reschedule (${formatScheduledDate(scheduledAt!, displayTz)})`
                      : 'Schedule'}
                  </button>
                  <div className="my-1 border-t border-line" />
                </>
              )}
            {isMobile && isInstagramPost && (imageUrl || imageAttachmentId) && (
              <>
                <button
                  onClick={() => {
                    track(AnalyticsEvents.Composer.COMPOSER_DOWNLOAD_TAPPED, {
                      creative_id: creative.id,
                      creative_type: creative.type,
                      format: 'image',
                    })
                    handleDownload()
                    setShowActions(false)
                  }}
                  disabled={isDownloading}
                  className="flex w-full items-center gap-2 px-3 py-2 text-[13px] text-paper-dim transition-colors hover:bg-bg-2 hover:text-paper disabled:opacity-50"
                >
                  <Download size={14} />
                  {isDownloading ? 'Downloading...' : 'Download'}
                </button>
                <div className="my-1 border-t border-line" />
              </>
            )}
            {/* Copy/Download for social posts (moved from header buttons) —
                hidden in Post mode (Schedule is the only action in this header). */}
            {isSocialPost && !isMobile && !postContext && (
              <>
                {isInstagramPost &&
                  (igContent?.caption || (igContent?.hashtags?.length ?? 0) > 0) && (
                    <>
                      {igContent?.caption && (
                        <button
                          onClick={() => {
                            handleIgCopy(igContent?.caption ?? '', 'caption')
                            setShowActions(false)
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-[13px] text-paper-dim transition-colors hover:bg-bg-2 hover:text-paper"
                        >
                          <Clipboard size={14} />
                          {igCopiedField === 'caption' ? 'Copied!' : 'Copy Caption'}
                        </button>
                      )}
                      {(igContent?.hashtags?.length ?? 0) > 0 && (
                        <button
                          onClick={() => {
                            handleIgCopy(
                              (igContent?.hashtags ?? [])
                                .slice(0, 5)
                                .map(h => `#${h}`)
                                .join(' '),
                              'hashtags'
                            )
                            setShowActions(false)
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-[13px] text-paper-dim transition-colors hover:bg-bg-2 hover:text-paper"
                        >
                          <Hash size={14} />
                          {igCopiedField === 'hashtags' ? 'Copied!' : 'Copy Hashtags'}
                        </button>
                      )}
                    </>
                  )}
                {isFacebookPost && fbContent?.message && (
                  <button
                    onClick={() => {
                      handleFbCopy()
                      setShowActions(false)
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-[13px] text-paper-dim transition-colors hover:bg-bg-2 hover:text-paper"
                  >
                    <Clipboard size={14} />
                    {fbCopied ? 'Copied!' : 'Copy Text'}
                  </button>
                )}
                {isLinkedInPost &&
                  (liContent?.caption || (liContent?.hashtags?.length ?? 0) > 0) && (
                    <>
                      {liContent?.caption && (
                        <button
                          onClick={() => {
                            handleLiCopy(liContent?.caption ?? '', 'caption')
                            setShowActions(false)
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-[13px] text-paper-dim transition-colors hover:bg-bg-2 hover:text-paper"
                        >
                          <Clipboard size={14} />
                          {liCopiedField === 'caption' ? 'Copied!' : 'Copy Text'}
                        </button>
                      )}
                      {(liContent?.hashtags?.length ?? 0) > 0 && (
                        <button
                          onClick={() => {
                            handleLiCopy(
                              (liContent?.hashtags ?? []).map(h => `#${h}`).join(' '),
                              'hashtags'
                            )
                            setShowActions(false)
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-[13px] text-paper-dim transition-colors hover:bg-bg-2 hover:text-paper"
                        >
                          <Hash size={14} />
                          {liCopiedField === 'hashtags' ? 'Copied!' : 'Copy Hashtags'}
                        </button>
                      )}
                    </>
                  )}
                <div className="my-1 border-t border-line" />
              </>
            )}
            <button
              onClick={handleDelete}
              className="flex w-full items-center gap-2 px-3 py-2 text-[13px] text-[color:var(--alert-error-text)] transition-colors hover:bg-[color:var(--alert-error-bg)]"
            >
              <Trash2 size={14} />
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Connect-to-publish gate (Post mode only). Mounted on demand by
       *  PostModal's `onConnectRequired` — we expose a Promise the modal
       *  awaits, then resolve with the final platform set or reject on
       *  dialog close. `requireAll` enforces composer's all-or-nothing
       *  contract: schedule is gated on every selected platform connecting,
       *  no "skip the rest" shortcut. */}
      {postContext && connectGate.open && (
        <ConnectToPublishDialog
          open={connectGate.open}
          onOpenChange={handleConnectGateOpenChange}
          missingPlatforms={connectGate.missing}
          selectedPlatforms={connectGate.selected}
          projectId={postContext.idea.projectId}
          onReady={handleConnectGateReady}
          requireAll
        />
      )}

      {/* Post-mode Schedule modal — the per-platform preview + review
       *  surface, scoped to the composer's strip selection (lockedPlatforms).
       *  Owns the date/time picker and the connect gate handoff above.
       *  When close carries `scheduled: true`, fire the one-shot acid pulse
       *  on the Schedule button via setJustScheduled (auto-resets after the
       *  pulse window so it doesn't replay on re-renders). */}
      {postContext && showSchedulePostModal && (
        <PostModal
          idea={postContext.idea}
          lockedPlatforms={selectedPlatforms}
          openedFrom="composer"
          entrySource={journeyEntrySource}
          intent={postModalIntent}
          onConnectRequired={handleConnectRequired}
          onClose={result => {
            setShowSchedulePostModal(false)
            setPostModalIntent('schedule')
            if (result?.scheduled) {
              setJustScheduled(true)
              setTimeout(() => setJustScheduled(false), 800)
            }
          }}
        />
      )}

      {/* Delete confirmation. Wording reflects whether we're nuking the whole
       *  Post (every platform sibling, via cascade) or just a standalone
       *  Creative; the button stays disabled while the request is in flight
       *  so the user can't double-fire. */}
      <Dialog
        open={showDeleteConfirm}
        onOpenChange={open => {
          if (isDeleting) return
          if (!open) setShowDeleteConfirm(false)
        }}
      >
        <DialogContent size="sm" variant="dark" withCloseButton>
          <DialogHeader>
            <DialogTitle variant="dark">
              {postContext ? 'Delete this post?' : 'Delete this creative?'}
            </DialogTitle>
            <DialogDescription variant="dark">
              {postContext
                ? 'All platform variants for this post will be permanently deleted. This cannot be undone.'
                : 'This creative will be permanently deleted. This cannot be undone.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              variant="t3-primary"
              size="t3-md"
              onClick={() => void handleConfirmDelete()}
              disabled={isDeleting}
              className="w-full"
            >
              {isDeleting ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Deleting…
                </>
              ) : (
                'Delete'
              )}
            </Button>
            <Button
              variant="t3-tertiary"
              size="t3-md"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
              className="w-full"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule dialog */}
      {/* Standalone (non-Post) creatives only: Post-context scheduling AND
       *  approval go through PostModal above, so `needsApproval` (which
       *  requires postContext) is never true here. */}
      <ScheduleDialog
        open={showScheduleDialog}
        onOpenChange={setShowScheduleDialog}
        onSchedule={handleSchedule}
        onUnschedule={isScheduledInFuture ? handleUnschedule : undefined}
        onPostNow={
          isScheduledInFuture
            ? () => {
                // Each handler runs its own gates, then takes the post off its
                // slot right before publishing (takeOffScheduleForManualPublish),
                // so a gate that bails leaves the schedule intact.
                setShowScheduleDialog(false)
                if (isInstagramPost) handlePostClick()
                else if (isFacebookPost) handleFbPostClick()
                else if (isTikTokVideo) handleTikTokPostClick()
                else if (isYouTubeShort) handleYouTubePostClick()
                else if (isLinkedInPost) handleLiPostClick()
                else if (isPinterestPin) handlePinPostClick()
              }
            : undefined
        }
        initialDate={scheduledDateParts?.date}
        initialTime={scheduledDateParts?.time}
      />

      {/* Mobile publish error (anchored to header) */}
      {isMobile && isInstagramPost && publishError && (
        <div className="absolute top-full right-2 z-20 mt-1 w-64 rounded-[10px] border border-line-bright bg-bg-3 p-3 shadow-[var(--shadow-card)]">
          <p className="text-[12px] text-[color:var(--alert-error-text)]">{publishError}</p>
          <button
            onClick={handlePostClick}
            className="mt-2 w-full rounded-[6px] border border-line-bright bg-bg-2 px-2 py-1.5 text-[12px] font-medium text-paper transition-colors hover:bg-bg-3"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Mobile LinkedIn publish error */}
      {isMobile && isLinkedInPost && liPublishError && (
        <div className="absolute top-full right-2 z-20 mt-1 w-64 rounded-[10px] border border-line-bright bg-bg-3 p-3 shadow-[var(--shadow-card)]">
          <p className="text-[12px] text-[color:var(--alert-error-text)]">{liPublishError}</p>
          <button
            onClick={handleLiPostClick}
            className="mt-2 w-full rounded-[6px] border border-line-bright bg-bg-2 px-2 py-1.5 text-[12px] font-medium text-paper transition-colors hover:bg-bg-3"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Mobile Facebook publish error */}
      {isMobile && isFacebookPost && fbPublishError && (
        <div className="absolute top-full right-2 z-20 mt-1 w-64 rounded-[10px] border border-line-bright bg-bg-3 p-3 shadow-[var(--shadow-card)]">
          <p className="text-[12px] text-[color:var(--alert-error-text)]">{fbPublishError}</p>
          <button
            onClick={handleFbPostClick}
            className="mt-2 w-full rounded-[6px] border border-line-bright bg-bg-2 px-2 py-1.5 text-[12px] font-medium text-paper transition-colors hover:bg-bg-3"
          >
            Try Again
          </button>
        </div>
      )}

      <Dialog open={showPostConfirm} onOpenChange={setShowPostConfirm}>
        <DialogContent size="sm" variant="dark" withCloseButton>
          <DialogHeader>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400">
              <Instagram size={24} className="text-white" />
            </div>
            <DialogTitle variant="dark" className="text-center">
              Post to Instagram
            </DialogTitle>
            <DialogDescription variant="dark" className="text-center">
              This will publish your content directly to your connected Instagram account.
            </DialogDescription>
          </DialogHeader>
          {publishError && (
            <p className="text-center text-[13px] text-[color:var(--alert-error-text)]">
              {publishError}
            </p>
          )}
          <button
            onClick={() => {
              setShowPostConfirm(false)
              handlePostClick()
            }}
            disabled={isPublishing}
            className="text-ink-on-acid flex w-full items-center justify-center gap-2 rounded-lg bg-acid px-4 py-2.5 text-[13px] font-semibold shadow-[var(--shadow-acid-cta)] transition-all hover:-translate-y-px disabled:opacity-50"
          >
            {isPublishing ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Posting...
              </>
            ) : (
              <>
                <Send size={16} />
                Post Now
              </>
            )}
          </button>
        </DialogContent>
      </Dialog>

      {tiktokContent && (
        <TikTokPostDialog
          open={showTikTokPostDialog}
          onOpenChange={setShowTikTokPostDialog}
          content={tiktokContent}
          isPublishing={isTikTokPublishing}
          publishError={tikTokPublishError}
          onPost={settings => void handlePublishToTikTok(settings)}
        />
      )}

      <PlatformConnectDialog
        open={tikTokPlatform.showConnectDialog}
        onOpenChange={tikTokPlatform.onDialogOpenChange}
        title="Connect TikTok"
        description="To post to TikTok, we first need to connect your account."
        icon={<span className="text-lg font-bold text-white">T</span>}
        iconBgClassName="bg-black"
        ctaLabel="Connect TikTok"
        ctaClassName="flex w-full items-center justify-center gap-2 rounded-lg bg-acid text-ink-on-acid hover:-translate-y-px px-4 py-2.5 text-[13px] font-semibold shadow-[var(--shadow-acid-cta)] transition-all disabled:opacity-50"
        errorMessage={tikTokPlatform.connectError}
        isConnecting={tikTokPlatform.isConnecting}
        onConnect={tikTokPlatform.handleConnect}
      />

      <Dialog
        open={showConnectDialog}
        onOpenChange={open => {
          if (!open && showConnectDialog) {
            track(AnalyticsEvents.Social.SOCIAL_CONNECT_DIALOG_DISMISSED, {
              platform: 'instagram',
              creative_id: creative.id,
              source: 'composer',
            })
          }
          setShowConnectDialog(open)
        }}
      >
        <DialogContent size="sm" variant="dark" withCloseButton>
          <DialogHeader>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400">
              <Instagram size={24} className="text-white" />
            </div>
            <DialogTitle variant="dark" className="text-center">
              Connect Instagram
            </DialogTitle>
            <DialogDescription variant="dark" className="text-center">
              To post to Instagram, we first need to connect your business account.
            </DialogDescription>
          </DialogHeader>
          {connectError && (
            <p className="text-center text-[13px] text-[color:var(--alert-error-text)]">
              {connectError}
            </p>
          )}
          <button
            onClick={handleConnectInstagram}
            disabled={isConnectingIg}
            className="text-ink-on-acid flex w-full items-center justify-center gap-2 rounded-lg bg-acid px-4 py-2.5 text-[13px] font-semibold shadow-[var(--shadow-acid-cta)] transition-all hover:-translate-y-px disabled:opacity-50"
          >
            {isConnectingIg ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <Instagram size={16} />
                Connect Instagram
              </>
            )}
          </button>
        </DialogContent>
      </Dialog>

      <PlatformConnectDialog
        open={fbPlatform.showConnectDialog}
        onOpenChange={fbPlatform.onDialogOpenChange}
        title="Connect Facebook"
        description="To post to Facebook, connect your account and select a Page."
        icon={<Send size={24} className="text-white" />}
        iconBgClassName="bg-[#1877F2]"
        ctaLabel="Connect Facebook"
        ctaClassName="flex w-full items-center justify-center gap-2 rounded-lg bg-acid text-ink-on-acid hover:-translate-y-px px-4 py-2.5 text-[13px] font-semibold shadow-[var(--shadow-acid-cta)] transition-all disabled:opacity-50"
        errorMessage={fbPlatform.connectError}
        isConnecting={fbPlatform.isConnecting}
        onConnect={fbPlatform.handleConnect}
      />

      {liPlatform.chooser}
      <PlatformConnectDialog
        open={liPlatform.showConnectDialog}
        onOpenChange={liPlatform.onDialogOpenChange}
        title="Connect LinkedIn"
        description="To post to LinkedIn, we first need to connect your account."
        icon={<Linkedin size={24} className="text-white" />}
        iconBgClassName="bg-[#0A66C2]"
        ctaLabel="Connect LinkedIn"
        ctaIcon={<Linkedin size={16} />}
        ctaClassName="flex w-full items-center justify-center gap-2 rounded-lg bg-[#0A66C2] px-4 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        errorMessage={liPlatform.connectError}
        isConnecting={liPlatform.isConnecting}
        onConnect={liPlatform.handleConnect}
      />

      <PlatformConnectDialog
        open={xPlatform.showConnectDialog}
        onOpenChange={xPlatform.onDialogOpenChange}
        title="Connect X"
        description="To post to X, we first need to connect your account."
        icon={<XLogo size={24} />}
        iconBgClassName="bg-gray-900 text-white"
        ctaLabel="Connect X"
        ctaIcon={<XLogo size={16} />}
        ctaClassName="flex w-full items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        errorMessage={xPlatform.connectError}
        errorClassName="text-center text-sm text-red-500"
        isConnecting={xPlatform.isConnecting}
        onConnect={xPlatform.handleConnect}
      />

      <PlatformConnectDialog
        open={pinPlatform.showConnectDialog}
        onOpenChange={pinPlatform.onDialogOpenChange}
        title="Connect Pinterest"
        description="To post to Pinterest, we first need to connect your account."
        icon={<PinterestIcon size={24} className="text-white" />}
        iconBgClassName="bg-[#E60023]"
        ctaLabel="Connect Pinterest"
        ctaIcon={<PinterestIcon size={16} />}
        ctaClassName="flex w-full items-center justify-center gap-2 rounded-lg bg-[#E60023] px-4 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        errorMessage={pinPlatform.connectError}
        isConnecting={pinPlatform.isConnecting}
        onConnect={pinPlatform.handleConnect}
      />

      <Dialog open={showYouTubeConnectDialog} onOpenChange={setShowYouTubeConnectDialog}>
        <DialogContent size="sm" variant="dark" withCloseButton>
          <DialogHeader>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#FF0000]">
              <Youtube size={24} className="text-white" />
            </div>
            <DialogTitle variant="dark" className="text-center">
              Connect YouTube
            </DialogTitle>
            <DialogDescription variant="dark" className="text-center">
              To post to YouTube, we first need to connect your YouTube channel.
            </DialogDescription>
          </DialogHeader>
          {youTubeConnectError && (
            <p className="text-center text-[13px] text-[color:var(--alert-error-text)]">
              {youTubeConnectError}
            </p>
          )}
          <button
            onClick={handleConnectYouTube}
            disabled={isConnectingYouTube}
            className="text-ink-on-acid flex w-full items-center justify-center gap-2 rounded-[8px] bg-acid px-4 py-2.5 text-[13px] font-semibold shadow-[var(--shadow-acid-cta)] transition-all hover:-translate-y-px disabled:opacity-50"
          >
            {isConnectingYouTube ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <Youtube size={16} />
                Connect YouTube
              </>
            )}
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={showYouTubeComingSoon} onOpenChange={setShowYouTubeComingSoon}>
        <DialogContent size="sm" variant="dark" withCloseButton>
          <DialogHeader>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-line-bright bg-bg-3 text-paper-dim">
              <Send size={24} />
            </div>
            <DialogTitle variant="dark" className="text-center">
              Coming Soon
            </DialogTitle>
            <DialogDescription variant="dark" className="text-center">
              Publishing to YouTube is coming soon. In the meantime, you can download your video and
              share it manually.
            </DialogDescription>
          </DialogHeader>
          <button
            onClick={() => {
              setShowYouTubeComingSoon(false)
              handleDownload()
            }}
            className="text-ink-on-acid flex w-full items-center justify-center gap-2 rounded-lg bg-acid px-4 py-2.5 text-[13px] font-semibold shadow-[var(--shadow-acid-cta)] transition-all hover:-translate-y-px"
          >
            <Download size={16} />
            Download Video
          </button>
        </DialogContent>
      </Dialog>
    </div>
  )
}
